import { useState, useRef, useEffect } from 'react'
import { Plus, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import type { TableView, TableViewConfig } from '@/admin/types/table-views'

interface ViewSwitcherProps {
  /** "classic" shows the All button + square tabs. "compact" shows pill-style saved views only (no All). */
  variant?: 'classic' | 'compact'
  views: TableView[]
  activeViewUuid: string | null
  onSelectView: (view: TableView | null) => void
  onSaveView: (name: string, config: TableViewConfig) => void
  onUpdateView: (uuid: string, config: TableViewConfig) => void
  onDeleteView: (uuid: string) => void
  onRenameView: (uuid: string, name: string) => void
  onSetOrgDefault?: (config: TableViewConfig) => void
  currentConfig: TableViewConfig
  canManageDefaults?: boolean
}

export function ViewSwitcher({
  variant = 'classic',
  views,
  activeViewUuid,
  onSelectView,
  onSaveView,
  onUpdateView,
  onDeleteView,
  onRenameView,
  onSetOrgDefault,
  currentConfig,
  canManageDefaults,
}: ViewSwitcherProps) {
  const { t } = useTranslation()
  const [isNaming, setIsNaming] = useState(false)
  const [newName, setNewName] = useState('')
  const [renamingUuid, setRenamingUuid] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const renameRef = useRef<HTMLInputElement>(null)
  // Latest pending debounced auto-save, flushed on unmount (below) so a change
  // is not lost when the user navigates away within the debounce window.
  const pendingUpdateRef = useRef<{ uuid: string; config: TableViewConfig } | null>(
    null,
  )
  const onUpdateViewRef = useRef(onUpdateView)
  useEffect(() => {
    onUpdateViewRef.current = onUpdateView
  }, [onUpdateView])

  // Focus input when naming or renaming
  useEffect(() => {
    if (isNaming && inputRef.current) inputRef.current.focus()
  }, [isNaming])

  useEffect(() => {
    if (renamingUuid && renameRef.current) renameRef.current.focus()
  }, [renamingUuid])

  function handleSave() {
    const name = newName.trim()
    if (!name) return
    onSaveView(name, currentConfig)
    setNewName('')
    setIsNaming(false)
  }

  function handleRename(uuid: string) {
    const name = renameValue.trim()
    const view = views.find((v) => v.uuid === uuid)
    setRenamingUuid(null)
    setRenameValue('')
    // Empty name asks to delete (via a non-dismissible confirm), so clicking
    // away can't nuke a view by accident. An unchanged name just closes.
    if (!name) {
      setDeletingUuid(uuid)
    } else if (view && name !== view.name) {
      onRenameView(uuid, name)
    }
  }

  /** Check if the active view's saved config differs from the current config */
  function hasUnsavedChanges(view: TableView): boolean {
    if (activeViewUuid !== view.uuid) return false
    const saved = view.config
    const current = currentConfig
    // Simple JSON comparison - good enough for shallow config objects
    return JSON.stringify(saved) !== JSON.stringify(current)
  }

  // Views are "just editable": auto-save filter/column changes to the active
  // view (debounced) so there is no explicit Save action or menu.
  useEffect(() => {
    const active = views.find((v) => v.uuid === activeViewUuid)
    if (!active || JSON.stringify(active.config) === JSON.stringify(currentConfig)) {
      pendingUpdateRef.current = null
      return
    }
    pendingUpdateRef.current = { uuid: active.uuid, config: currentConfig }
    const id = setTimeout(() => {
      onUpdateView(active.uuid, currentConfig)
      pendingUpdateRef.current = null
    }, 600)
    return () => clearTimeout(id)
  }, [currentConfig, activeViewUuid, views, onUpdateView])

  // Flush a pending debounced save on unmount so navigating away mid-debounce
  // does not discard the change.
  useEffect(() => {
    return () => {
      const pending = pendingUpdateRef.current
      if (pending) onUpdateViewRef.current(pending.uuid, pending.config)
    }
  }, [])

  const isCompact = variant === 'compact'
  const deletingView = deletingUuid ? views.find((v) => v.uuid === deletingUuid) : null

  return (
    <div className="cv-data-table flex min-w-0 flex-nowrap items-center gap-1">
      {/* "All" tab - only in classic mode */}
      {!isCompact && (
        <Button
          variant="ghost"
          size="xs"
          onClick={() => onSelectView(null)}
          className={`cv-data-table-compact h-auto cursor-pointer shrink-0 rounded-[4px] border-[0.5px] border-solid px-3 py-1 font-normal uppercase tracking-[0.08em] transition-all duration-150 ${
            activeViewUuid === null
              ? 'bg-[var(--green-bg)] text-[var(--green)] border-[var(--green-border,var(--green))] hover:bg-[var(--green-bg)] hover:text-[var(--green)]'
              : 'text-[var(--ink-3)] border-[var(--rule)] hover:bg-[var(--bg-subtle)]'
          }`}
        >
          {t('common.all', { defaultValue: 'All' })}
        </Button>
      )}
      {/* Saved views */}
      {views.map((view) => {
        const isActive = activeViewUuid === view.uuid
        const unsaved = hasUnsavedChanges(view)

        return (
          <div key={view.uuid} className="relative shrink-0 group/tab">
            {renamingUuid === view.uuid ? (
              <Input
                ref={renameRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => handleRename(view.uuid)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRename(view.uuid)
                  if (e.key === 'Escape') {
                    setRenamingUuid(null)
                    setRenameValue('')
                  }
                }}
                className="cv-data-table-compact w-[120px] rounded-[4px] border-[0.5px] border-[var(--green)] bg-[var(--bg)] px-3 py-1 uppercase tracking-[0.08em] text-[var(--ink)] outline-none"
              />
            ) : isCompact ? (
              /* Compact: sentence-case pill with × dismiss */
              (<div
                className={`cv-data-table-body inline-flex max-w-[160px] shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 py-1 transition-all duration-[120ms]${
                  isActive
                    ? 'border-[0.5px] border-transparent bg-[var(--green)] text-white'
                    : 'border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]'
                }`}
                onClick={() => onSelectView(view)}
                onDoubleClick={() => {
                  setRenamingUuid(view.uuid)
                  setRenameValue(view.name)
                }}
                title={view.name}
              >
                <span className="truncate">{view.name}</span>
                <span
                  className="shrink-0 text-lg leading-none opacity-40 transition-opacity duration-100 hover:opacity-90"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDeletingUuid(view.uuid)
                  }}
                >
                  &times;
                </span>
              </div>)
            ) : (
              /* Classic: square tab with uppercase */
              (<Button
                variant="ghost"
                size="xs"
                onClick={() => onSelectView(view)}
                onDoubleClick={() => {
                  setRenamingUuid(view.uuid)
                  setRenameValue(view.name)
                }}
                className={`cv-data-table-compact h-auto max-w-[160px] cursor-pointer items-center gap-0 rounded-[4px] border-[0.5px] border-solid px-3 py-1 font-normal uppercase tracking-[0.08em] transition-all duration-150 ${
                  isActive
                    ? 'bg-[var(--green-bg)] text-[var(--green)] border-[var(--green-border,var(--green))] hover:bg-[var(--green-bg)] hover:text-[var(--green)]'
                    : 'text-[var(--ink-3)] border-[var(--rule)] hover:bg-[var(--bg-subtle)]'
                }`}
                title={view.name}
              >
                {view.is_org_default ? (
                  <Star className="mr-1 size-2.5 shrink-0 fill-current" aria-label="Org default" />
                ) : canManageDefaults && onSetOrgDefault ? (
                  <span
                    role="button"
                    aria-label="Set as org default"
                    title="Set as org default"
                    onClick={(e) => {
                      e.stopPropagation()
                      onSetOrgDefault(view.config)
                    }}
                    className="mr-1 flex shrink-0 cursor-pointer opacity-0 transition-opacity duration-100 group-hover/tab:opacity-100"
                  >
                    <Star className="size-2.5" />
                  </span>
                ) : null}
                <span className="truncate">{view.name}</span>
                {unsaved && (
                  <span
                    className="ml-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--green)]"
                    title="Unsaved changes"
                  />
                )}
              </Button>)
            )}
          </div>
        );
      })}
      {/* New view input or button */}
      {isNaming ? (
        <Input
          ref={inputRef}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onBlur={() => {
            if (!newName.trim()) {
              setIsNaming(false)
              setNewName('')
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSave()
            }
            if (e.key === 'Escape') {
              setIsNaming(false)
              setNewName('')
            }
          }}
          placeholder="View name, then enter"
          className={`outline-none ${
            isCompact
              ? 'cv-data-table-body w-[160px] rounded-full border-[0.5px] border-[var(--green)] bg-[var(--bg)] px-3.5 py-1 text-[var(--ink)]'
              : 'cv-data-table-compact w-[160px] rounded-[4px] border-[0.5px] border-[var(--green)] bg-[var(--bg)] px-3 py-1 uppercase tracking-[0.08em] text-[var(--ink)]'
          }`}
        />
      ) : (
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => setIsNaming(true)}
          className="h-6 w-6 flex items-center justify-center rounded-full transition-all duration-150 cursor-pointer shrink-0 hover:bg-[var(--bg-subtle)] text-[var(--ink-3)] border-[0.5px] border-solid border-[var(--rule)]"
          title={t('common.saveView', { defaultValue: 'Save current view' })}
        >
          <Plus className="size-3" />
        </Button>
      )}
      {deletingView && (
        <ConfirmDeleteDialog
          open={!!deletingUuid}
          onOpenChange={(o) => {
            if (!o) setDeletingUuid(null)
          }}
          dismissible={false}
          title={t('viewSwitcher.deleteTitle', {
            defaultValue: 'Delete "{{name}}"?',
            name: deletingView.name,
          })}
          description={t('viewSwitcher.deleteDescription', {
            defaultValue: "This removes the saved view and can't be undone.",
          })}
          onConfirm={() => {
            onDeleteView(deletingView.uuid)
            setDeletingUuid(null)
          }}
        />
      )}
    </div>
  );
}
